import type { AgentSession, ProvisionStep } from '@/api-models/agent-sessions';

/**
 * Everything the UI says about a session's sandbox lifecycle, derived in one
 * place.
 *
 * There used to be three copies of the paused/resuming wording — two in
 * `AgentSessionPage` and one in `ThreadColumn` — plus a header pill derived
 * separately from `sandbox_status`, which is how the header could read "Starting"
 * while the page read "Resuming session…". The pill and the composer's status
 * line now both come from {@link sessionPhase}, so they cannot disagree.
 */
export type SessionPhase =
  | 'planning'
  | 'starting'
  | 'preparing'
  | 'resuming'
  | 'ready'
  | 'stopping'
  | 'paused'
  | 'archived'
  | 'budget'
  | 'error';

export type StatusTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

export interface SessionStatusLine {
  phase: SessionPhase;
  /** The sentence rendered directly above the composer textarea. */
  text: string;
  /** Secondary detail; currently only the sandbox error message. */
  detail: string | null;
  tone: StatusTone;
  spinner: boolean;
  /** Inline recovery action, or null when there's nothing to do. */
  action: 'Resume' | 'Try again' | null;
  /** Whether the composer accepts input. */
  canSend: boolean;
}

export interface SessionPillStyle {
  label: string;
  wrap: string;
  dot: string;
}

type StatusInput = Pick<
  AgentSession,
  | 'status'
  | 'sandbox_status'
  | 'provision_step'
  | 'sandbox_id'
  | 'sandbox_error_message'
  | 'budget_exhausted_at'
  | 'archived_at'
  | 'mode'
>;

/** Human-readable label per backend provisioning stage. The raw step string never reaches a user. */
export const PROVISION_STEP_LABEL: Record<ProvisionStep, string> = {
  snapshot: 'Preparing the sandbox image',
  create: 'Creating the sandbox',
  sandbox_started: 'Sandbox started — bringing it online',
  health_check: 'Sandbox started — waiting for it to come online',
  clone: 'Sandbox online — cloning the repository',
  setup_commands: "Sandbox online — running the project's setup commands",
  resume_start: 'Resuming the paused sandbox',
  fast_forward: 'Catching the repository up to the latest commits',
  inject_key: 'Reconnecting the agent',
};

/** Steps written only by the resume path — see `get_or_resume_session`. */
const RESUME_STEPS: ReadonlySet<string> = new Set<ProvisionStep>(['resume_start', 'fast_forward', 'inject_key']);

/** Steps where the sandbox is up and we're preparing the workspace inside it. */
const PREPARING_STEPS: ReadonlySet<string> = new Set<ProvisionStep>(['clone', 'setup_commands']);

export function sessionPhase(session: StatusInput): SessionPhase {
  // Error and stopping override `status`: `record_error` writes
  // sandbox_status = error while status may still read provisioning.
  if (session.status === 'error' || session.sandbox_status === 'error') return 'error';
  // Archiving outranks every sandbox state below it, including the `stopping` it
  // causes. The backend refuses resume and prompt for an archived session, so any
  // phase that offers Resume or leaves the composer open would be advertising an
  // action the server rejects. Restore is the only way forward.
  if (session.archived_at) return 'archived';
  // An exhausted budget outranks the sandbox states it causes: the server stops
  // the sandbox on the way to refusing work, and reporting that as an idle pause
  // ("paused after 5 minutes of inactivity") would misstate why it stopped and
  // offer a Resume the server will refuse.
  if (session.budget_exhausted_at) return 'budget';
  if (session.sandbox_status === 'stopping') return 'stopping';
  if (session.status === 'hibernated') return 'paused';
  // Checked after the failure states so a planning session that errored still reads as an error,
  // but before every sandbox state below — a plan session has no sandbox, and describing one
  // would be describing something that does not exist.
  if (session.mode === 'plan') return 'planning';

  if (session.status === 'provisioning') {
    const step = session.provision_step;
    if (step && RESUME_STEPS.has(step)) return 'resuming';
    // A resume that hasn't written its first step yet still has a sandbox.
    if (!step && session.sandbox_id) return 'resuming';
    if (step && PREPARING_STEPS.has(step)) return 'preparing';
    return 'starting';
  }

  return 'ready';
}

export function sessionStatusLine(session: StatusInput): SessionStatusLine {
  const phase = sessionPhase(session);
  const step = session.provision_step;

  switch (phase) {
    case 'error':
      return {
        phase,
        text: 'The sandbox failed to start.',
        detail: session.sandbox_error_message,
        tone: 'danger',
        spinner: false,
        action: 'Try again',
        canSend: false,
      };
    case 'stopping':
      return {
        phase,
        text: 'Stopping the sandbox…',
        detail: null,
        tone: 'warning',
        spinner: true,
        action: null,
        canSend: true,
      };
    case 'budget':
      return {
        phase,
        text: 'Budget limit reached — the agent is stopped.',
        detail: 'Raise the session budget to continue.',
        tone: 'danger',
        spinner: false,
        // Deliberately no Resume: the server refuses both resume and prompt while
        // the cap is spent, so the only real action is raising the limit (the
        // budget chip in the header).
        action: null,
        canSend: false,
      };
    case 'archived':
      return {
        phase,
        text: 'This session is archived — its sandbox is stopped.',
        detail: 'Restore it to pick the work back up.',
        tone: 'neutral',
        spinner: false,
        // No Resume, for the same reason as `budget`: the server refuses it.
        // Restoring happens from the session actions menu.
        action: null,
        canSend: false,
      };
    case 'paused':
      return {
        phase,
        text: 'Sandbox paused after 5 minutes of inactivity.',
        detail: null,
        tone: 'neutral',
        spinner: false,
        action: 'Resume',
        // Sending is what resumes it — the prompt goes over REST, which restarts
        // the sandbox transparently.
        canSend: true,
      };
    case 'planning':
      return {
        phase,
        text: 'Planning — no sandbox running',
        detail: 'The agent is reading the repository read-only. Start coding to provision a sandbox.',
        tone: 'neutral',
        spinner: false,
        action: null,
        canSend: true,
      };
    case 'ready':
      return {
        phase,
        text: 'Sandbox ready',
        detail: null,
        tone: 'success',
        spinner: false,
        action: null,
        canSend: true,
      };
    default: {
      // starting | preparing | resuming — all spinner states differing only in copy.
      const label = step
        ? PROVISION_STEP_LABEL[step]
        : phase === 'resuming'
          ? 'Resuming the paused sandbox'
          : 'Starting the sandbox';
      return {
        phase,
        text: `${label}…`,
        detail: null,
        tone: 'info',
        spinner: true,
        action: null,
        canSend: true,
      };
    }
  }
}

/** Header / row status pill per phase, rendered by `SessionStatusPill`. */
export const SESSION_PHASE_PILL: Record<SessionPhase, SessionPillStyle> = {
  planning: { wrap: 'bg-surface-3 text-fg-secondary', dot: 'bg-fg-tertiary', label: 'Plan' },
  starting: { wrap: 'bg-info-tint text-info', dot: 'bg-info', label: 'Starting' },
  preparing: { wrap: 'bg-info-tint text-info', dot: 'bg-info', label: 'Starting' },
  resuming: { wrap: 'bg-info-tint text-info', dot: 'bg-info', label: 'Starting' },
  ready: { wrap: 'bg-success-tint text-success', dot: 'bg-success', label: 'Running' },
  stopping: { wrap: 'bg-warning-tint text-warning', dot: 'bg-warning', label: 'Stopping' },
  paused: { wrap: 'bg-surface-3 text-fg-secondary', dot: 'bg-fg-tertiary', label: 'Paused' },
  archived: { wrap: 'bg-surface-3 text-fg-secondary', dot: 'bg-fg-tertiary', label: 'Archived' },
  budget: { wrap: 'bg-danger-tint text-danger', dot: 'bg-danger', label: 'Budget reached' },
  error: { wrap: 'bg-danger-tint text-danger', dot: 'bg-danger', label: 'Error' },
};

/**
 * Who a session is waiting on, for list views.
 *
 * {@link sessionPhase} answers "what is the sandbox doing", which is not the question someone
 * scanning a list of sessions is asking — a `ready` sandbox looks identical whether the agent is
 * mid-run or finished an hour ago. This answers "is it my move": the agent is `working`, it
 * `needs` a human, the work is `done` (merged), or it is `idle`.
 */
export type SessionActivity = 'working' | 'asking' | 'needs' | 'done' | 'idle' | 'error';

export interface SessionActivityMark {
  kind: SessionActivity;
  /** Short status word for the row's meta line. */
  label: string;
  tone: StatusTone;
}

type ActivityInput = StatusInput &
  Pick<
    AgentSession,
    | 'initial_prompt'
    | 'initial_prompt_delivered_at'
    | 'last_turn_role'
    | 'last_turn_at'
    | 'last_active_at'
    | 'awaiting_user_input_at'
  >;

/**
 * How long an unanswered prompt may sit before the run behind it is presumed dead.
 *
 * A backend restart, a crashed loop or a sandbox archived out from under one all leave the same
 * trace: a user turn with no assistant turn after it, forever. Without this every such session
 * would spin "Working" for weeks. Generous on purpose — a genuinely long tool-heavy run must
 * never be called stalled while it is still going.
 *
 * Only the fallback now: it is a guess about a *turn*, and a turn is far too coarse a unit to ask
 * about liveness. `last_active_at` answers the question directly wherever it is set.
 */
const STALE_RUN_MS = 30 * 60_000;

/**
 * How long a liveness stamp stays believable.
 *
 * A running agent stamps `agent_session.last_active_at` at least every `ACTIVITY_TOUCH_INTERVAL`
 * (10s, `routes/sandbox/agent.rs`), so this is a generous multiple of the write cadence rather
 * than a judgement about how long work should take: nine missed touches means the loop is gone,
 * not that the model is being slow. A loop that exits cleanly clears the column outright, so this
 * only has to cover the crash case — a killed backend, an OOM, a sandbox pulled out from under it.
 */
const AGENT_LIVE_MS = 90_000;

/** The PR state a row needs; a subset of `TeamSessionPullRequest`. */
interface ActivityPull {
  state: 'open' | 'closed' | 'merged' | 'draft';
}

/**
 * Whether a bound agent loop is still working on this session.
 *
 * `last_active_at` is the direct answer — the server stamps it from the agent's own event stream
 * and clears it when the loop ends. Everything else here is inference, and the reason a healthy
 * session used to read "Stalled": turns are only persisted when a turn *completes*, so
 * `last_turn_at` does not move for the whole of a long tool-heavy run, and any run outliving
 * {@link STALE_RUN_MS} was called dead while it was streaming tool calls.
 *
 * The turn-age fallback still covers rows no loop has ever stamped: a queued session whose loop
 * hasn't bound yet, and sessions that predate the column being written.
 */
function agentAlive(session: Pick<ActivityInput, 'last_active_at' | 'last_turn_at'>): boolean {
  if (session.last_active_at != null) {
    return Date.now() - Date.parse(session.last_active_at) < AGENT_LIVE_MS;
  }
  return session.last_turn_at != null && Date.now() - Date.parse(session.last_turn_at) < STALE_RUN_MS;
}

export function sessionActivity(session: ActivityInput, pull?: ActivityPull | null): SessionActivityMark {
  const phase = sessionPhase(session);

  if (phase === 'error') return { kind: 'error', label: 'Failed', tone: 'danger' };
  if (phase === 'budget') return { kind: 'needs', label: 'Budget reached', tone: 'danger' };
  if (phase === 'starting' || phase === 'preparing' || phase === 'resuming') {
    return { kind: 'working', label: 'Starting', tone: 'info' };
  }

  // The agent asked something and stopped. This has to outrank every turn-role check below,
  // because nothing else here can see it: an agent parked on a question has just written an
  // assistant turn, exactly like one that finished replying — and those mean opposite things to
  // the person reading the row. Deliberately no staleness cutoff either, unlike a running prompt:
  // a question is durable by design and is still worth answering weeks later, which is the whole
  // reason the agent stops rather than blocks.
  if (session.awaiting_user_input_at) {
    return { kind: 'asking', label: 'Needs answer', tone: 'warning' };
  }

  // A prompt is persisted as a user turn the moment it is submitted; the assistant turn only
  // lands when the run completes. So a trailing user turn means the agent still has the ball —
  // true whether the run started here, in another tab, or with nobody watching at all.
  if (session.last_turn_role === 'user') {
    // …unless nothing is left running to answer it. A paused sandbox took its agent loop down
    // with it, and an unanswered prompt with no live loop behind it outlived whatever was working
    // on it. Both are a stalled run the user has to restart, the opposite of "leave it, it's busy".
    const runnable = phase === 'ready' || phase === 'planning';
    return runnable && agentAlive(session)
      ? { kind: 'working', label: 'Working', tone: 'info' }
      : { kind: 'needs', label: 'Stalled', tone: 'warning' };
  }
  if (session.initial_prompt && !session.initial_prompt_delivered_at) {
    return { kind: 'working', label: 'Queued', tone: 'info' };
  }

  // Merged outranks "the agent replied": once the branch has landed, whatever it said last is
  // history, not a question waiting on an answer.
  if (pull?.state === 'merged') return { kind: 'done', label: 'Merged', tone: 'success' };
  if (session.last_turn_role === 'assistant') return { kind: 'needs', label: 'Your turn', tone: 'warning' };
  if (phase === 'paused') return { kind: 'idle', label: 'Paused', tone: 'neutral' };
  if (phase === 'planning') return { kind: 'idle', label: 'Plan', tone: 'neutral' };
  return { kind: 'idle', label: 'Idle', tone: 'neutral' };
}
